/**
 * The curriculum as ONE ordered walk of lessons and quizzes.
 *
 * The server hands the player two separate lists: chapters (each with its
 * lessons, in curriculum order) and tests ordered by `created_at` with a
 * `chapter_id` that IS their placement. Interleaving them here is what lets
 * "Next" stop skipping chapter quizzes — and keying on `chapterId` rather than
 * the tests array order is what keeps a course whose final test was authored
 * first from coming out backwards.
 *
 * Important: a quiz is a CERTIFICATE gate, never a progression gate. A learner
 * whose attempts are exhausted, or whose essay is still pending review, can
 * never pass it — so those are skipped rather than blocking the course forever.
 * A PRACTICE test is not even a certificate gate: it is offered once in the
 * walk and then stepped over, however it went.
 */
export type SequenceLesson = {
  id: string;
  title: string;
  playable: boolean;
  isPreview: boolean;
  sourceType: string;
  durationSeconds: number | null;
  /** Server-resolved drip verdict for THIS learner (decoration + this walk). */
  locked?: boolean;
  availableAt?: string | null;
};

export type SequenceChapter = { id: string; title: string; lessons: SequenceLesson[] };

export type SequenceTest = {
  id: string;
  chapterId: string | null;
  title: string;
  passed: boolean;
  canAttempt: boolean;
  /** Practice tests are optional: offered once, never a wall. */
  isPractice?: boolean;
  /** How many attempts this learner has already made (practice pacing). */
  attemptCount?: number;
};

export type SequenceItem =
  | { kind: 'lesson'; id: string; lesson: SequenceLesson; chapterId: string }
  | { kind: 'quiz'; id: string; test: SequenceTest };

export type NextVerdict =
  | { kind: 'lesson'; lesson: SequenceLesson }
  | { kind: 'locked'; lesson: SequenceLesson; availableAt: string | null }
  | { kind: 'quiz'; test: SequenceTest }
  | { kind: 'end' };

export function buildCourseSequence(
  chapters: readonly SequenceChapter[],
  tests: readonly SequenceTest[],
): SequenceItem[] {
  const out: SequenceItem[] = [];
  for (const chapter of chapters) {
    for (const lesson of chapter.lessons) {
      out.push({ kind: 'lesson', id: lesson.id, lesson, chapterId: chapter.id });
    }
    for (const test of tests.filter((t) => t.chapterId === chapter.id)) {
      out.push({ kind: 'quiz', id: test.id, test });
    }
  }
  // The final test (no chapter) always closes the course.
  for (const test of tests.filter((t) => t.chapterId == null)) {
    out.push({ kind: 'quiz', id: test.id, test });
  }
  return out;
}

/** A drip lock whose moment has passed is not a lock — the clock alone clears it. */
function stillLocked(lesson: SequenceLesson, now: number): boolean {
  if (!lesson.locked) return false;
  if (!lesson.availableAt) return true;
  const at = new Date(lesson.availableAt).getTime();
  return Number.isNaN(at) ? true : at > now;
}

export function resolveNext({
  sequence,
  currentId,
  now = Date.now(),
}: {
  sequence: readonly SequenceItem[];
  currentId: string | null;
  now?: number;
}): NextVerdict {
  const start = sequence.findIndex((item) => item.id === currentId);
  if (start === -1) return { kind: 'end' };
  for (let i = start + 1; i < sequence.length; i += 1) {
    const item = sequence[i] as SequenceItem;
    if (item.kind === 'quiz') {
      // A practice test is never "passed" in a way that closes it — its
      // `canAttempt` stays true forever — so pacing on attempts instead is what
      // stops Next from parking on it permanently. Offered once, then carried
      // past; the sidebar keeps it reachable.
      if (item.test.isPractice) {
        if ((item.test.attemptCount ?? 0) === 0) return { kind: 'quiz', test: item.test };
        continue;
      }
      if (!item.test.passed && item.test.canAttempt) return { kind: 'quiz', test: item.test };
      continue;
    }
    if (stillLocked(item.lesson, now)) {
      return { kind: 'locked', lesson: item.lesson, availableAt: item.lesson.availableAt ?? null };
    }
    if (!item.lesson.playable) continue;
    return { kind: 'lesson', lesson: item.lesson };
  }
  return { kind: 'end' };
}

/** Previous walks lessons only — nobody wants Back to drop them into a quiz. */
export function resolvePrev({
  sequence,
  currentId,
  now = Date.now(),
}: {
  sequence: readonly SequenceItem[];
  currentId: string | null;
  now?: number;
}): SequenceLesson | null {
  const start = sequence.findIndex((item) => item.id === currentId);
  if (start <= 0) return null;
  for (let i = start - 1; i >= 0; i -= 1) {
    const item = sequence[i] as SequenceItem;
    if (item.kind !== 'lesson') continue;
    if (!item.lesson.playable || stillLocked(item.lesson, now)) continue;
    return item.lesson;
  }
  return null;
}
